import { InfoIcon } from 'lucide-react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import {
  FacebookIntegration,
  InstagramIntegration,
  IntegrationType,
} from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

type MetaIntegration = FacebookIntegration | InstagramIntegration;

type IncomingMessagesForm = { ignore_story_interactions: boolean };

interface Props {
  integration: MetaIntegration;
}

const FORM_ID = 'meta-incoming-messages-form';

/**
 * Story replies exist on both Messenger and Instagram, story mentions only on Instagram,
 * so the toggle is offered for both channels with channel-specific copy.
 */
const IncomingMessagesCard = ({ integration }: Props) => {
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  const { updateIntegration, updateStatus } = useIntegrations<MetaIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );

  const savedValue = integration?.config?.ignore_story_interactions ?? false;

  const { control, handleSubmit } = useForm<IncomingMessagesForm>({
    defaultValues: { ignore_story_interactions: savedValue },
  });

  const ignoreStoryInteractions = useWatch({ control, name: 'ignore_story_interactions' });
  const isDirty = ignoreStoryInteractions !== savedValue;

  const isInstagram = integration?.type === IntegrationType.INSTAGRAM;
  const subtitle = isInstagram
    ? t('integrations.facebook.incoming_messages.story.subtitle_instagram')
    : t('integrations.facebook.incoming_messages.story.subtitle_facebook');

  const onSubmit = (values: IncomingMessagesForm) => {
    updateIntegration({
      ...integration,
      config: {
        ...integration.config,
        ignore_story_interactions: values.ignore_story_interactions,
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
      <div className="mb-4 w-xl">
        <div className="mb-2 flex items-center justify-between">
          <p className="px-1.5 text-sm leading-none font-medium text-muted-foreground">
            {t('integrations.facebook.incoming_messages.title')}
          </p>
        </div>
        <FieldsetCard
          isDirty={isDirty}
          isLoading={updateStatus === 'pending'}
          disabled={!isDirty || !canWrite}
          saveButtonLabel={t('common.save')}
          formId={FORM_ID}
        >
          <div className="flex flex-col gap-4">
            <Controller
              name="ignore_story_interactions"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Switch
                  variant="description"
                  switchPosition="right"
                  label={t('integrations.facebook.incoming_messages.story.title')}
                  description={subtitle}
                  checked={value}
                  onCheckedChange={onChange}
                  disabled={!canWrite || updateStatus === 'pending'}
                />
              )}
            />
            {ignoreStoryInteractions && (
              <Alert variant="info">
                <InfoIcon />
                <AlertDescription>
                  {t('integrations.facebook.incoming_messages.story.hint')}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </FieldsetCard>
      </div>
    </form>
  );
};

export default IncomingMessagesCard;
